import React from "react";
import { EmailFormat } from "../types/emailFormat.types";
import { toast } from "react-hot-toast";

const downloadFile = (base64: string, fileName: string, mimeType: string) => {
  try {
    // Validate base64 string
    if (!base64 || typeof base64 !== "string") {
      throw new Error("Invalid base64 string");
    }

    // Test base64 decoding
    let byteCharacters;
    try {
      byteCharacters = atob(base64.trim());
    } catch (error) {
      throw new Error("Failed to decode base64 string");
    }

    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create Blob with appropriate MIME type
    const blob = new Blob([byteArray], { type: mimeType });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "attachment";
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    //console.error("Error downloading file:", error);
    toast.error("Failed to download attachment");
  }
};

const getFileInfo = (base64: string, subject: string) => {
  let mimeType = "application/octet-stream"; // Default for unknown types
  let extension = "bin";
  let isImage = false;

  // Validate base64 input
  if (!base64 || typeof base64 !== "string") {
    //
    // console.warn("Invalid or empty base64 string:", base64);
    return {
      mimeType,
      extension,
      fileName: `${subject.replace(/\s+/g, "_")}.${extension}`,
      isImage,
    };
  }

  // Normalize base64 string
  let cleanedBase64 = base64.trim().replace(/\s+/g, "");
  // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
  const dataUrlMatch = cleanedBase64.match(/^data:[^;]+;base64,(.+)/);
  if (dataUrlMatch) {
    cleanedBase64 = dataUrlMatch[1];
  }
  // Ensure proper base64 padding
  const paddingNeeded = cleanedBase64.length % 4;
  if (paddingNeeded) {
    cleanedBase64 += "=".repeat(4 - paddingNeeded);
  }

  // Test base64 decoding
  let isValidBase64 = true;
  let decodedString = "";
  try {
    decodedString = atob(cleanedBase64);
  } catch (error) {
    isValidBase64 = false;
    console.warn("Base64 decoding failed:", error);
  }

  // Debug raw and cleaned base64
  // console.log("Base64 debug:", {
  //   rawPrefix: base64.slice(0, 50),
  //   cleanedPrefix: cleanedBase64.slice(0, 50),
  //   length: cleanedBase64.length,
  //   subject,
  //   isValidBase64,
  // });

  // Common base64 signatures (case-insensitive)
  const lowerBase64 = cleanedBase64.toLowerCase();
  if (isValidBase64) {
    if (cleanedBase64.startsWith("JVBER") || lowerBase64.startsWith("jvber")) {
      mimeType = "application/pdf";
      extension = "pdf";
    } else if (cleanedBase64.startsWith("/9j/")) {
      mimeType = "image/jpeg";
      extension = "jpg";
      isImage = true;
    } else if (cleanedBase64.startsWith("iVBOR")) {
      mimeType = "image/png";
      extension = "png";
      isImage = true;
    } else if (cleanedBase64.startsWith("R0lGOD")) {
      mimeType = "image/gif";
      extension = "gif";
      isImage = true;
    } else if (
      cleanedBase64.startsWith("UEsDB") ||
      lowerBase64.startsWith("uesdb")
    ) {
      // Office Open XML (XLSX, DOCX, PPTX)
      const hasExcel =
        decodedString.includes("xl/workbook.xml") ||
        decodedString.includes("xl/");
      const hasWord = decodedString.includes("word/");
      const hasPowerPoint = decodedString.includes("ppt/");
      const hasExcelContentType = decodedString.includes(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"
      );

      // //console.log("Office Open XML detection:", {
      //   hasExcel,
      //   hasWord,
      //   hasPowerPoint,
      //   hasExcelContentType,
      // });

      if (hasExcel || hasExcelContentType) {
        mimeType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        extension = "xlsx";
      } else if (hasWord) {
        mimeType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        extension = "docx";
      } else if (hasPowerPoint) {
        mimeType =
          "application/vnd.openxmlformats-officedocument.presentationml.presentation";
        extension = "pptx";
      } else {
        // Fallback for unrecognized Office Open XML (ZIP-based) files
        mimeType = "application/zip";
        extension = "zip";
        //console.warn("UEsDB detected but no specific Office Open XML subtype found (xl/, word/, ppt/). Falling back to .zip.");
      }
    } else {
      console.warn(
        "No matching file signature found for base64 string:",
        cleanedBase64.slice(0, 50)
      );
    }
  }

  const fileName = `${subject.replace(/\s+/g, "_")}.${extension}`;
  // console.log("Detected file:", { mimeType, extension, fileName, isImage });
  return { mimeType, extension, fileName, isImage };
};

export const getEmailFormatTableColumns = (
  handleEdit: (emailFormat: EmailFormat) => void
) => [
  {
    name: "Type Name",
    selector: (row: EmailFormat) => row.typeName,
    sortable: true,
    cell: (row: EmailFormat) => (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <div
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "rgb(167 133 144)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "10px",
            fontWeight: "bold",
          }}
        >
          {row.typeName.charAt(0).toUpperCase()}
        </div>
        <span style={{ fontWeight: "500", color: "#33475b" }}>
          {row.typeName}
        </span>
      </div>
    ),
    width: "200px",
  },
  {
    name: "Subject",
    selector: (row: EmailFormat) => row.subject,
    sortable: true,
  },
  {
    name: "Attachment",
    selector: (row: EmailFormat) => row.attachment || "",
    sortable: false,
    cell: (row: EmailFormat) => {
      if (!row.attachment) {
        return <span className="text-muted">No Attachment</span>;
      }

      const { mimeType, extension, fileName, isImage } = getFileInfo(
        row.attachment,
        row.subject
      );

      return (
        <div className="d-flex align-items-center gap-2">
          {/*          
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => downloadFile(row.attachment, fileName, mimeType)}
          >
            <i className="bi bi-download me-1"></i>
            Download
          </button>

           <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick={() => downloadFile(row.attachment, fileName, mimeType)
            
            style={{ color: "#007bff", textDecoration: "none" }}
          >
            Download
          </a>
          
          */}

          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              downloadFile(row.attachment, fileName, mimeType);
            }}
            style={{ color: "#007bff", textDecoration: "none" }}
          >
            <i className="bi bi-download me-1"></i>
            Download
          </a>
        </div>
      );
    },
    width: "200px",
  },
  {
    name: "",
    cell: (row: EmailFormat) => (
      <div className="action-cell">
        <button
          className="action-button edit-button"
          title="Edit Email Format"
          onClick={() => handleEdit(row)}
        >
          <i className="bi bi-pencil-square"></i>
        </button>
        <button className="action-button more-button" title="More Actions">
          <i className="bi bi-three-dots"></i>
        </button>
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "60px",
  },
];
